import type { ChannelClient } from '@hibro/platform-core';
import type { AppLink, IAppLinkService, LaunchHosting } from '@hibro/shared';
import { APP_LINKS_CHANGED_EVENT } from '@hibro/shared';
import {
  Button,
  ConfirmDialog,
  Icon,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@hibro/ui-client';
import {
  type ChangeEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { AppLinkIcon } from '../app-links/icon';
import { useTranslator } from '../i18n/i18n';

/** Cap for a downsampled data-URI icon so the KV store stays bounded (见 21). */
const ICON_DATA_URI_MAX = 48_000;
const ICON_SIZE = 64;

export interface AppLinksSectionProps {
  appLinkService: IAppLinkService;
  client: ChannelClient;
}

interface Draft {
  name: string;
  url: string;
  icon: string;
  hosting: LinkAppHosting;
}

type LinkAppHosting = Exclude<LaunchHosting, 'miniapp'>;

const EMPTY_DRAFT: Draft = {
  name: '',
  url: '',
  icon: '',
  hosting: 'inline',
};

const HOSTING_OPTIONS: readonly {
  value: LinkAppHosting;
  labelKey:
    | 'settings.appLinks.hosting.inline'
    | 'settings.appLinks.hosting.window'
    | 'settings.appLinks.hosting.browser';
}[] = [
  { value: 'inline', labelKey: 'settings.appLinks.hosting.inline' },
  { value: 'window', labelKey: 'settings.appLinks.hosting.window' },
  { value: 'browser', labelKey: 'settings.appLinks.hosting.browser' },
];

function isHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Downsample a picked image to a small square data URI (见 21). Both runtimes
 * have canvas; no main-process file store is required.
 */
function fileToIconDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read image'));
    reader.onload = () => {
      const dataUri = reader.result;
      if (typeof dataUri !== 'string') {
        reject(new Error('Could not read image'));
        return;
      }
      const img = new Image();
      img.onerror = () => reject(new Error('Could not decode image'));
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = ICON_SIZE;
        canvas.height = ICON_SIZE;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas unavailable'));
          return;
        }
        const scale = Math.max(ICON_SIZE / img.width, ICON_SIZE / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, (ICON_SIZE - w) / 2, (ICON_SIZE - h) / 2, w, h);
        const uri = canvas.toDataURL('image/png');
        if (uri.length > ICON_DATA_URI_MAX) {
          reject(new Error('Icon is too large after resize'));
          return;
        }
        resolve(uri);
      };
      img.src = dataUri;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Mechanism-level link-app management (见 21): add / edit / remove user links.
 * Organisation-pushed links are listed but not editable.
 */
export function AppLinksSection({
  appLinkService,
  client,
}: AppLinksSectionProps): ReactNode {
  const { t } = useTranslator();
  const [links, setLinks] = useState<AppLink[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [removeTarget, setRemoveTarget] = useState<AppLink | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(() => {
    appLinkService
      .list()
      .then((next) => {
        setLinks(next);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [appLinkService]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    return client.listen(APP_LINKS_CHANGED_EVENT, () => {
      refresh();
    });
  }, [client, refresh]);

  const startAdd = (): void => {
    setAdding(true);
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
  };

  const startEdit = (link: AppLink): void => {
    setAdding(false);
    setEditingId(link.id);
    setDraft({
      name: link.name,
      url: link.url,
      icon: link.icon ?? '',
      hosting: link.hosting === 'miniapp' ? 'inline' : link.hosting,
    });
  };

  const cancelForm = (): void => {
    setAdding(false);
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
  };

  const saveForm = (): void => {
    const name = draft.name.trim();
    const url = draft.url.trim();
    if (!name || !isHttpUrl(url)) {
      setError(t('settings.appLinks.invalidUrl'));
      return;
    }
    const icon = draft.icon.trim();
    const input = {
      name,
      url,
      hosting: draft.hosting,
      ...(icon ? { icon } : {}),
    };
    setBusy(true);
    setError(null);
    const op =
      editingId !== null
        ? appLinkService.update(editingId, input)
        : appLinkService.add(input).then(() => undefined);
    op.then(() => {
      cancelForm();
      refresh();
    })
      .catch((err: unknown) => {
        setError(
          t('settings.appLinks.error', {
            message: err instanceof Error ? err.message : String(err),
          }),
        );
      })
      .finally(() => setBusy(false));
  };

  const onPickIcon = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    void fileToIconDataUri(file)
      .then((uri) => setDraft((prev) => ({ ...prev, icon: uri })))
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      });
  };

  const formOpen = adding || editingId !== null;

  return (
    <div className="flex flex-col gap-3 py-2">
      {error ? (
        <p className="m-0 text-[13px] text-destructive">{error}</p>
      ) : null}

      {links.length === 0 && !formOpen ? (
        <p className="m-0 text-[13px] text-muted-foreground">
          {t('settings.appLinks.empty')}
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {links.map((link) => (
            <li
              key={`${link.sourceId}:${link.id}`}
              className="flex items-center gap-2 rounded-lg px-1 py-1"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-(--app-color-bg)">
                <AppLinkIcon
                  spec={
                    link.icon
                      ? { type: 'resource', light: link.icon }
                      : undefined
                  }
                  size={24}
                />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium">
                  {link.name}
                </div>
                <div className="truncate text-[11px] text-muted-foreground">
                  {link.editable ? link.url : t('settings.appLinks.managed')}
                </div>
              </div>
              {link.editable ? (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => startEdit(link)}
                  >
                    {t('settings.appLinks.edit')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => setRemoveTarget(link)}
                  >
                    {t('settings.appLinks.remove')}
                  </Button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {formOpen ? (
        <div className="flex flex-col gap-2 rounded-lg border border-(--app-color-border-subtle) p-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="app-link-name">{t('settings.appLinks.name')}</Label>
            <Input
              id="app-link-name"
              value={draft.name}
              disabled={busy}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, name: event.target.value }))
              }
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="app-link-url">{t('settings.appLinks.url')}</Label>
            <Input
              id="app-link-url"
              value={draft.url}
              disabled={busy}
              placeholder={t('settings.appLinks.url')}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, url: event.target.value }))
              }
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label>{t('settings.appLinks.hosting')}</Label>
            <Select
              value={draft.hosting}
              disabled={busy}
              onValueChange={(value) => {
                if (!value) {
                  return;
                }
                setDraft((prev) => ({
                  ...prev,
                  hosting: value,
                }));
              }}
            >
              <SelectTrigger>
                <SelectValue>
                  {(value) =>
                    t(
                      HOSTING_OPTIONS.find((option) => option.value === value)
                        ?.labelKey ?? 'settings.appLinks.hosting.inline',
                    )
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {HOSTING_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="app-link-icon">{t('settings.appLinks.icon')}</Label>
            <div className="flex items-center gap-2">
              <Input
                id="app-link-icon"
                value={draft.icon.startsWith('data:') ? '' : draft.icon}
                disabled={busy}
                placeholder={t('settings.appLinks.icon')}
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, icon: event.target.value }))
                }
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPickIcon}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => fileInputRef.current?.click()}
              >
                {t('settings.appLinks.iconPick')}
              </Button>
              {draft.icon ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => setDraft((prev) => ({ ...prev, icon: '' }))}
                >
                  {t('settings.appLinks.iconClear')}
                </Button>
              ) : null}
            </div>
            {draft.icon.startsWith('data:') ? (
              <div className="flex items-center gap-2 pt-1">
                <Icon
                  spec={{ type: 'resource', light: draft.icon }}
                  fallbackText={draft.name || '?'}
                  size={24}
                />
                <span className="text-[11px] text-muted-foreground">
                  data URI
                </span>
              </div>
            ) : null}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={cancelForm}
            >
              {t('settings.appLinks.cancel')}
            </Button>
            <Button type="button" size="sm" disabled={busy} onClick={saveForm}>
              {t('settings.appLinks.save')}
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={startAdd}
          >
            {t('settings.appLinks.add')}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={removeTarget !== null}
        title={t('settings.appLinks.remove')}
        message={
          removeTarget
            ? t('settings.appLinks.confirmRemove', { name: removeTarget.name })
            : undefined
        }
        confirmText={t('settings.appLinks.remove')}
        cancelText={t('settings.appLinks.cancel')}
        destructive
        onConfirm={() => {
          if (!removeTarget) {
            return;
          }
          const id = removeTarget.id;
          setRemoveTarget(null);
          setBusy(true);
          appLinkService
            .remove(id)
            .then(refresh)
            .catch((err: unknown) => {
              setError(
                t('settings.appLinks.error', {
                  message: err instanceof Error ? err.message : String(err),
                }),
              );
            })
            .finally(() => setBusy(false));
        }}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
}
